/**
 * Panel de enrolamiento TOTP (app autenticadora).
 *
 * Firebase exige email verificado antes de enrolar MFA nativo
 * (auth/unverified-email), así que el panel tiene dos etapas:
 *   1. Si el email no está verificado: enviar el correo de verificación y
 *      esperar a que el usuario confirme (botón "Ya verifiqué" → reload).
 *   2. Generar el secreto (API modular sobre la app compat), mostrar QR +
 *      clave manual, y confirmar con el primer código de la app. El método
 *      queda registrado server-side vía la callable finalizeTotpEnrollment.
 *
 * Se usa tanto en el enrolamiento forzado (enroll/index.js) como en la
 * activación voluntaria (mfa/security_settings.js).
 */

import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import { Form, Input, Button, Spin, Typography, notification } from 'antd';
import { SafetyOutlined, MailOutlined } from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';

import { mfa } from '../../../actions/auth/mfa';

const FormItem = Form.Item;
const { Text, Paragraph } = Typography;

export const MfaEnrollTotp = ({ onEnrolled }) => {

  const dispatch = useDispatch();
  const firebase = useFirebase();

  const [emailVerified, setEmailVerified] = useState(
    () => firebase.auth().currentUser?.emailVerified === true
  );
  const [verificationSent, setVerificationSent] = useState(false);
  const [checking, setChecking] = useState(false);
  const [enrollment, setEnrollment] = useState(null); // { secret, qrUrl, secretKey }
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const startedRef = useRef(false);

  // Genera el secreto apenas el email esté verificado
  useEffect(() => {
    if (!emailVerified || startedRef.current) return;
    startedRef.current = true;
    dispatch( mfa.startTotpEnrollment() )
      .then( setEnrollment )
      .catch( error => {
        if ( error.code === 'auth/unverified-email' ) {
          startedRef.current = false;
          setEmailVerified(false);
        } else if ( error.code === 'auth/requires-recent-login' ) {
          notification.warning({
            message: 'Sesión antigua',
            description: 'Por seguridad, cierre sesión e inicie de nuevo antes de activar la verificación',
            placement: 'topRight'
          });
        } else {
          notification.error({
            message: 'No se pudo iniciar la activación',
            description: error?.message,
            placement: 'topRight'
          });
        }
      });
  }, [emailVerified, dispatch]);

  const sendVerification = async () => {
    try {
      await firebase.auth().currentUser.sendEmailVerification();
      setVerificationSent(true);
      notification.success({
        message: 'Correo enviado',
        description: 'Abra el enlace de verificación que llegó a su correo y luego presione "Ya verifiqué"',
        placement: 'topRight'
      });
    } catch ( error ) {
      notification.warning({
        message: 'No se pudo enviar el correo',
        description: error?.message,
        placement: 'topRight'
      });
    }
  };

  const checkVerified = async () => {
    setChecking(true);
    try {
      await firebase.auth().currentUser.reload();
      const verified = firebase.auth().currentUser?.emailVerified === true;
      setEmailVerified(verified);
      if (!verified) {
        notification.info({
          message: 'Aún sin verificar',
          description: 'Abra el enlace del correo de verificación primero',
          placement: 'topRight'
        });
      }
    } finally {
      setChecking(false);
    }
  };

  const onConfirm = async values => {
    setLoading(true);
    try {
      await dispatch( mfa.finishTotpEnrollment( enrollment.secret, values.code ) );
      notification.success({
        message: 'Verificación en 2 pasos activada',
        description: 'A partir de ahora se pedirá el código de su app al iniciar sesión',
        placement: 'topRight'
      });
      onEnrolled && onEnrolled();
    } catch ( error ) {
      const isBadCode = error.code === 'auth/invalid-verification-code'
        || error.code === 'auth/totp-challenge-timeout';
      notification.warning({
        message: isBadCode ? 'Código incorrecto' : 'No se pudo activar',
        description: isBadCode
          ? 'Verifique el código de su app de autenticación e intente de nuevo'
          : error?.message,
        placement: 'topRight'
      });
      setLoading(false);
    }
  };

  // Etapa 1: verificación de email pendiente
  if (!emailVerified) {
    return (
      <div>
        <Paragraph>
          Para usar una app de autenticación primero debe verificar su
          dirección de correo.
        </Paragraph>
        <Button type = "primary" icon = { <MailOutlined /> } onClick = { sendVerification } >
          { verificationSent ? 'Reenviar correo de verificación' : 'Enviar correo de verificación' }
        </Button>
        { verificationSent &&
          <Button style = {{ marginLeft: 8 }} loading = { checking } onClick = { checkVerified } >
            Ya verifiqué
          </Button> }
      </div>
    );
  }

  // Etapa 2: generando el secreto
  if (!enrollment) {
    return <Spin tip = "Generando código QR..." ><div style = {{ minHeight: 120 }} /></Spin>;
  }

  // Etapa 3: escanear QR y confirmar el primer código
  return (
    <div>
      <Paragraph>
        Escanee este código QR con su app de autenticación
        (Google Authenticator, Microsoft Authenticator, etc.):
      </Paragraph>
      <div style = {{ textAlign: 'center', margin: '16px 0' }} >
        <QRCodeSVG value = { enrollment.qrUrl } size = { 180 } />
      </div>
      <Paragraph>
        O ingrese esta clave manualmente: <Text code copyable > { enrollment.secretKey } </Text>
      </Paragraph>
      <Form form = { form } onFinish = { onConfirm } >
        <FormItem name = "code" label = "Código de la app"
          rules = {[
            { required: true, message: 'Debe ingresar el código' },
            { pattern: /^[0-9]{6}$/, message: 'El código tiene 6 dígitos' }
          ]} >
          <Input
            maxLength = { 6 }
            inputMode = "numeric"
            placeholder = "000000"
            style = {{ maxWidth: 200 }}
            prefix = { <SafetyOutlined style = {{ color: 'rgba(0,0,0,.25)' }} /> } />
        </FormItem>
        <Button type = "primary" htmlType = "submit" loading = { loading } >
          Activar
        </Button>
      </Form>
    </div>
  );
};
